import { useState, useEffect, useRef } from "react";

const API_BASE = "https://radioin-backend-production.up.railway.app/api";
const WS_BASE  = "wss://radioin-backend-production.up.railway.app";

export default function Remote() {
  const storeIdFromUrl = new URLSearchParams(window.location.search).get("store");

  // ── Remoto multi-lojas: ?stores=id1,id2,... (guardado p/ PWA do iOS, que perde a URL)
  const UUID_RE = /^[0-9a-f]{8}-[0-9a-f]{4}-[0-9a-f]{4}-[0-9a-f]{4}-[0-9a-f]{12}$/i;
  const storesFromUrl = (new URLSearchParams(window.location.search).get("stores") || "")
    .split(",").map(x => x.trim()).filter(x => UUID_RE.test(x));
  if (storesFromUrl.length) localStorage.setItem("radioin_remote_stores", storesFromUrl.join(","));

  // iOS PWA não preserva query params — recupera do localStorage se necessário
  const storeId = storeIdFromUrl || storesFromUrl[0] || localStorage.getItem("radioin_remote_store");
  const savedMulti = (storesFromUrl.length ? storesFromUrl : (localStorage.getItem("radioin_remote_stores") || "").split(","))
    .filter(x => UUID_RE.test(x));
  const multiIds = savedMulti.includes(storeId) ? savedMulti : []; // só vale se a loja atual está na lista

  // Salva no localStorage sempre que vier pela URL
  if (storeIdFromUrl && storeIdFromUrl !== localStorage.getItem("radioin_remote_store")) {
    localStorage.setItem("radioin_remote_store", storeIdFromUrl);
  }

  // ── Manifest via backend — URL real que o iOS Safari respeita ─────────────
  useEffect(() => {
    if (!storeId) return;
    const existing = document.querySelector("link[rel='manifest']");
    if (existing) existing.remove();
    const link = document.createElement("link");
    link.rel  = "manifest";
    link.href = `https://radioin-backend-production.up.railway.app/api/remote-manifest/${storeId}?origin=${encodeURIComponent(window.location.origin)}`;
    document.head.appendChild(link);
  }, [storeId]);

  const [phase, setPhase]       = useState("pin");   // pin | remote
  const [pin, setPin]           = useState("");
  const [pinError, setPinError] = useState("");
  const [pinLoading, setPinLoading] = useState(false);

  const [storeName, setStoreName]     = useState("");
  const [connected, setConnected]     = useState(false);
  const [playerState, setPlayerState] = useState(null); // {title, playing, volume}

  const [announcements, setAnnouncements] = useState([]);
  const [announcementCategories, setAnnouncementCategories] = useState([]);
  const [openCategories, setOpenCategories] = useState({});
  const [savedSearch, setSavedSearch] = useState(""); // busca na aba Salvas
  const [ttsText, setTtsText]   = useState("");
  const [ttsVoice, setTtsVoice] = useState("Aoede");
  const [ttsStyle, setTtsStyle] = useState("warm");
  const [ttsSending, setTtsSending] = useState(false);
  const [ttsMsg, setTtsMsg]     = useState("");
  const [ttsUrl, setTtsUrl]     = useState(null);
  // 📅 Programar a locução gerada (horários fixos ou rodízio de uma categoria)
  const [ttsLast, setTtsLast]   = useState(null); // { url, text, voice, style, duration }
  const [schedOpen, setSchedOpen] = useState(false);
  const [schedForm, setSchedForm] = useState({ title: "", mode: "schedule", times: [], timeInput: "", days: ["0","1","2","3","4","5","6"], categoryId: "", until: "" });
  const [schedSaving, setSchedSaving] = useState(false);
  const [schedMsg, setSchedMsg] = useState("");

  // ─── Gravação de voz ──────────────────────────────────────────────────────
  const [recState, setRecState]   = useState("idle");
  const [recUrl, setRecUrl]       = useState(null);
  const [recBlob, setRecBlob]     = useState(null);
  const [recMsg, setRecMsg]       = useState("");
  const [recSavedUrl, setRecSavedUrl] = useState(null); // URL salva após envio — para repetir
  const mediaRecRef  = useRef(null);
  const chunksRef    = useRef([]);

  const [tab, setTab]           = useState("controls"); // controls | tts | car | saved | grades

  // ─── Anúncio de veículos (aba 🚗 Veículo) ─────────────────────────────────
  const [carOpts, setCarOpts]     = useState(null);   // { ready, tipos, marcas, cores }
  const [carForm, setCarForm]     = useState({ tipo: "carro", marca: "", modelo: "", cor: "", placa: "", gongo: true, repeticoes: 0 });
  const [carSending, setCarSending] = useState(false);
  const [carMsg, setCarMsg]       = useState("");
  const [carLastUrl, setCarLastUrl] = useState(null); // último anúncio — repetir sem remontar
  const [volume, setVolume]     = useState(80);
  const [schedules, setSchedules] = useState([]);
  const [switchingSchedule, setSwitchingSchedule] = useState(null); // id sendo trocado
  const [confirmSwitch, setConfirmSwitch] = useState(null); // { id, name } aguardando confirmação

  const wsRef          = useRef(null);
  const pingRef        = useRef(null);
  const pongTimeoutRef = useRef(null); // detecta conexão zumbi
  const storeToken     = useRef(null); // PIN validado

  // ─── Multi-lojas: nomes p/ o seletor + troca de loja ──────────────────────
  const [multiStores, setMultiStores] = useState([]); // [{id, name}]
  useEffect(() => {
    if (multiIds.length < 2) return;
    fetch(`${API_BASE}/remote/stores?ids=${multiIds.join(",")}`)
      .then(r => r.json()).then(d => setMultiStores(Array.isArray(d) ? d : [])).catch(() => {});
  }, []); // eslint-disable-line

  // Troca de loja: reabre o remoto da outra loja. O PIN fica só nesta aba
  // (sessionStorage, apagado ao fechar) para entrar sozinho — nunca vai no link.
  function switchStore(id) {
    if (!id || id === storeId) return;
    try { if (pin.length === 4) sessionStorage.setItem("radioin_remote_pin", pin); } catch {}
    localStorage.setItem("radioin_remote_store", id);
    window.location.href = `${window.location.pathname}?store=${id}&stores=${multiIds.join(",")}`;
  }

  const autoPinTried = useRef(false);
  useEffect(() => {
    if (autoPinTried.current || multiIds.length < 2) return;
    autoPinTried.current = true;
    let saved = null;
    try { saved = sessionStorage.getItem("radioin_remote_pin"); } catch {}
    if (saved && /^\d{4}$/.test(saved)) { setPin(saved); submitPin(saved, true); }
  }, []); // eslint-disable-line

  // ─── Valida PIN ────────────────────────────────────────────────────────────
  async function submitPin(pinValue, auto = false) {
    const pinToUse = typeof pinValue === "string" ? pinValue : pin;
    if (pinToUse.length !== 4) return setPinError("O PIN deve ter 4 dígitos");
    setPinLoading(true); setPinError("");
    try {
      const res = await fetch(`${API_BASE}/remote/auth`, {
        method: "POST",
        headers: { "Content-Type": "application/json" },
        body: JSON.stringify({ storeId, pin: pinToUse }),
      });
      const data = await res.json();
      if (!res.ok) {
        if (auto) { setPin(""); throw new Error("Esta loja usa outro PIN. Digite o PIN dela."); }
        throw new Error(data.error || "PIN inválido");
      }
      if (multiIds.length > 1) { try { sessionStorage.setItem("radioin_remote_pin", pinToUse); } catch {} }
      storeToken.current = data.token;
      setStoreName(data.storeName);
      setAnnouncements(data.announcements || []);
      setAnnouncementCategories(data.announcementCategories || []);
      setVolume(100);
      // Carrega grades disponíveis
      fetch(`${API_BASE}/player/${storeId}/schedules`)
        .then(r => r.json()).then(d => setSchedules(Array.isArray(d) ? d : [])).catch(() => {});
      setPhase("remote");
    } catch (e) { setPinError(e.message); }
    setPinLoading(false);
  }

  // ─── WebSocket robusto com detecção de conexão zumbi ──────────────────────
  // Guarda contra vazamento de conexões ("Insufficient resources"):
  //  - cleanupWS() remove handlers ANTES de fechar, evitando ws.onclose
  //    disparar reconnect duplicado.
  //  - scheduleReconnect() usa flag `reconnectInFlight` para garantir
  //    que nunca haja mais de 1 reconexão agendada.
  //  - Identity check (wsRef.current !== ws) faz callbacks de WS antigos
  //    virarem no-ops quando um novo WS já tomou o lugar.
  //  - Exponential backoff (1s → 30s) evita hammering quando servidor cai.
  useEffect(() => {
    if (phase !== "remote") return;

    let dead = false;                 // true após unmount — bloqueia qualquer ação
    let reconnectTimer = null;        // handle do setTimeout de reconexão
    let reconnectInFlight = false;    // true enquanto reconexão está agendada
    let wsReconnectAttempt = 0;       // contador para backoff exponencial

    const BACKOFF_MS = [1000, 2000, 4000, 8000, 15000, 30000];

    function clearAllTimers() {
      clearInterval(pingRef.current);        pingRef.current = null;
      clearTimeout(pongTimeoutRef.current);  pongTimeoutRef.current = null;
      clearTimeout(reconnectTimer);          reconnectTimer = null;
    }

    function cleanupWS() {
      const ws = wsRef.current;
      if (!ws) return;
      // CRUCIAL: remover handlers antes de close() impede que ws.onclose
      // dispare uma nova reconexão — é o que causava vazamento de 2+ WS
      // por cada reconexão.
      ws.onopen = null;
      ws.onmessage = null;
      ws.onclose = null;
      ws.onerror = null;
      try {
        if (ws.readyState === WebSocket.CONNECTING || ws.readyState === WebSocket.OPEN) {
          ws.close();
        }
      } catch {}
      wsRef.current = null;
    }

    function scheduleReconnect() {
      if (dead || reconnectInFlight) return;
      reconnectInFlight = true;
      wsReconnectAttempt++;
      const delay = BACKOFF_MS[Math.min(wsReconnectAttempt - 1, BACKOFF_MS.length - 1)];
      console.log(`[RadioIN WS] Reconectando em ${delay}ms (tentativa ${wsReconnectAttempt})`);
      reconnectTimer = setTimeout(() => {
        reconnectInFlight = false;
        connect();
      }, delay);
    }

    function forceReconnect() {
      if (dead) return;
      clearAllTimers();
      cleanupWS();
      setConnected(false);
      scheduleReconnect();
    }

    function connect() {
      if (dead) return;
      cleanupWS(); // garantia extra: nenhum WS antigo pendurado
      const ws = new WebSocket(`${WS_BASE}/ws/remote?store=${storeId}&token=${storeToken.current}&role=remote`);
      wsRef.current = ws;

      ws.onopen = () => {
        // Se este ws já foi substituído (reconexão relâmpago), ignora
        if (dead || wsRef.current !== ws) { try { ws.close(); } catch {} return; }
        setConnected(true);
        wsReconnectAttempt = 0; // sucesso → reseta backoff

        // Ping a cada 20s + espera pong em até 8s; se não vier, conexão é zumbi
        pingRef.current = setInterval(() => {
          if (ws.readyState !== WebSocket.OPEN || wsRef.current !== ws) return;
          try { ws.send(JSON.stringify({ type: "ping" })); } catch {}
          pongTimeoutRef.current = setTimeout(() => {
            console.warn("[RadioIN WS] Pong não chegou — conexão zumbi, reconectando...");
            forceReconnect();
          }, 8000);
        }, 20000);
      };

      ws.onmessage = (e) => {
        if (dead || wsRef.current !== ws) return;
        try {
          const msg = JSON.parse(e.data);
          // Qualquer mensagem do servidor confirma que a conexão está viva
          clearTimeout(pongTimeoutRef.current);
          pongTimeoutRef.current = null;
          if (msg.type === "player_state") {
            setPlayerState(msg.data);
            if (typeof msg.data?.volume === "number") setVolume(msg.data.volume);
          }
        } catch {}
      };

      ws.onclose = () => {
        if (dead) return;
        // Callback de WS antigo (já substituído) — ignora
        if (wsRef.current !== ws) return;
        clearInterval(pingRef.current);        pingRef.current = null;
        clearTimeout(pongTimeoutRef.current);  pongTimeoutRef.current = null;
        setConnected(false);
        wsRef.current = null;
        scheduleReconnect();
      };

      ws.onerror = () => {
        // Deixa o onclose cuidar — chamar close() aqui causava race condition
      };
    }

    connect();

    // ── Reconecta imediatamente quando o celular volta ao foco ──────────────
    // Registrado em 3 eventos (visibilitychange, focus, pageshow) porque
    // diferentes browsers/PWAs disparam um ou outro. A guard `reconnectInFlight`
    // garante que múltiplos disparos não criam reconexões duplicadas.
    function handleVisibilityChange() {
      if (dead || document.visibilityState !== "visible") return;
      const ws = wsRef.current;
      if (!ws || ws.readyState === WebSocket.CLOSED || ws.readyState === WebSocket.CLOSING) {
        if (!reconnectInFlight) {
          console.log("[RadioIN WS] Tela voltou ao foco — reconectando...");
          forceReconnect();
        }
      } else if (ws.readyState === WebSocket.OPEN) {
        // Conexão parece aberta — confirma com ping antes de marcar como viva
        try { ws.send(JSON.stringify({ type: "ping" })); } catch {}
        clearTimeout(pongTimeoutRef.current);
        pongTimeoutRef.current = setTimeout(() => {
          if (dead || wsRef.current !== ws) return;
          console.warn("[RadioIN WS] Pong não chegou ao voltar ao foco — reconectando...");
          forceReconnect();
        }, 5000);
      }
    }

    document.addEventListener("visibilitychange", handleVisibilityChange);
    window.addEventListener("focus", handleVisibilityChange);
    window.addEventListener("pageshow", handleVisibilityChange);

    return () => {
      dead = true;
      clearAllTimers();
      cleanupWS();
      window.removeEventListener("focus", handleVisibilityChange);
      window.removeEventListener("pageshow", handleVisibilityChange);
      document.removeEventListener("visibilitychange", handleVisibilityChange);
    };
  }, [phase]);

  // ─── Envia comando ao player via WebSocket ─────────────────────────────────
  function send(payload) {
    if (wsRef.current?.readyState === 1) {
      wsRef.current.send(JSON.stringify(payload));
    }
  }

  function cmdVolume(v) {
    setVolume(v);
    send({ type: "cmd", action: "volume", value: v / 100 });
  }

  function cmdSkip()    { send({ type: "cmd", action: "skip" }); }
  function cmdToggle()  { send({ type: "cmd", action: "toggle" }); }

  // ─── Locução cadastrada → toca no player ──────────────────────────────────
  function playAnnouncement(ann) {
    send({ type: "cmd", action: "play_announcement", url: ann.bunny_url, title: ann.title });
  }

  // ─── Aviso de manutenção (ligado pelo Master) ─────────────────────────────
  const [notice, setNotice] = useState({ tts: false, car: false, message: "" });
  useEffect(() => {
    if (phase !== "remote" || (tab !== "tts" && tab !== "car" && tab !== "controls")) return;
    fetch(`${API_BASE}/remote/notice`).then(r => r.json()).then(d => d && setNotice(d)).catch(() => {});
  }, [phase, tab]);
  const NoticeBox = ({ on }) => on ? (
    <div style={{ padding: "12px 14px", background: "rgba(255,187,51,0.1)", border: "1px solid rgba(255,187,51,0.4)", borderRadius: 10, color: "#ffbb33", fontSize: 13, lineHeight: 1.5, textAlign: "center" }}>
      🛠 {notice.message || "Estamos atualizando o sistema. Esta função volta em breve."}
    </div>
  ) : null;

  // ─── Anúncio de veículos ──────────────────────────────────────────────────
  async function loadCarOptions() {
    try {
      const r = await fetch(`${API_BASE}/remote/car/options?storeId=${encodeURIComponent(storeId)}&token=${encodeURIComponent(storeToken.current || "")}`);
      const d = await r.json();
      if (!r.ok) throw new Error(d.error || "Erro ao carregar");
      setCarOpts(d);
    } catch (e) { setCarOpts({ ready: false, tipos: [], marcas: [], cores: [], error: e.message }); }
  }
  useEffect(() => { if (tab === "car" && !carOpts) loadCarOptions(); }, [tab]); // eslint-disable-line

  const placaNorm = (carForm.placa || "").toUpperCase().replace(/[^A-Z0-9]/g, "");
  const placaOk = /^[A-Z]{3}[0-9][A-Z0-9][0-9]{2}$/.test(placaNorm);

  async function sendCar() {
    if (!placaOk || carSending || notice.car) return;
    setCarSending(true); setCarMsg("");
    try {
      const res = await fetch(`${API_BASE}/remote/car`, {
        method: "POST",
        headers: { "Content-Type": "application/json" },
        body: JSON.stringify({ storeId, token: storeToken.current, ...carForm, placa: placaNorm }),
      });
      const data = await res.json();
      if (!res.ok) throw new Error(data.error || "Erro ao anunciar");
      setCarLastUrl(data.url);
      setCarMsg(data.sent ? "✓ Anúncio tocando na loja!" : "✗ Player da loja está offline");
      setTimeout(() => setCarMsg(""), 5000);
    } catch (e) { setCarMsg("✗ " + e.message); }
    setCarSending(false);
  }

  function repeatCar() {
    if (!carLastUrl) return;
    send({ type: "cmd", action: "play_announcement", url: carLastUrl, title: "🚗 Anúncio de veículo" });
    setCarMsg("✓ Repetindo anúncio!");
    setTimeout(() => setCarMsg(""), 3000);
  }

  // ─── TTS instantâneo ──────────────────────────────────────────────────────
  async function sendTts() {
    if (!ttsText.trim() || notice.tts) return;
    setTtsSending(true); setTtsMsg("");
    try {
      const res = await fetch(`${API_BASE}/remote/tts`, {
        method: "POST",
        headers: { "Content-Type": "application/json" },
        body: JSON.stringify({ storeId, token: storeToken.current, text: ttsText, voice: ttsVoice, style: ttsStyle }),
      });
      const data = await res.json();
      if (!res.ok) throw new Error(data.error || "Erro ao gerar locução");
      setTtsUrl(data.url); // Guarda URL para repetir sem gastar Gemini
      setTtsLast({ url: data.url, text: ttsText, voice: ttsVoice, style: ttsStyle, duration: data.duration });
      setSchedOpen(false); setSchedMsg("");
      setSchedForm(f => ({ ...f, title: ttsText.trim().replace(/\s+/g, " ").slice(0, 40), times: [], timeInput: "", until: "" }));
      setTtsMsg("✓ Locução enviada! Áudio guardado para repetir.");
      setTimeout(() => setTtsMsg(""), 4000);
    } catch (e) { setTtsMsg("✗ " + e.message); }
    setTtsSending(false);
  }

  // ─── 📅 Programar a locução gerada ────────────────────────────────────────
  async function saveSchedTts() {
    if (!ttsLast || schedSaving) return;
    const f = schedForm;
    if (f.mode === "schedule" && !f.times.length) return setSchedMsg("✗ Adicione pelo menos um horário");
    if (f.mode === "schedule" && !f.days.length) return setSchedMsg("✗ Escolha pelo menos um dia");
    setSchedSaving(true); setSchedMsg("");
    try {
      const res = await fetch(`${API_BASE}/remote/tts/save`, {
        method: "POST", headers: { "Content-Type": "application/json" },
        body: JSON.stringify({
          storeId, token: storeToken.current, url: ttsLast.url, text: ttsLast.text, voice: ttsLast.voice, style: ttsLast.style,
          duration: ttsLast.duration, title: f.title, mode: f.mode, times: f.times, days: f.days, categoryId: f.categoryId || null,
          ends_at: f.until ? new Date(f.until + "T23:59:00").toISOString() : null,
        }),
      });
      const data = await res.json();
      if (!res.ok) throw new Error(data.error || "Erro ao programar");
      if (data.announcement) setAnnouncements(prev => [data.announcement, ...prev]);
      setSchedMsg(f.mode === "schedule"
        ? `✓ Programada: ${f.times.join(", ")}${f.until ? " até " + new Date(f.until + "T12:00:00").toLocaleDateString("pt-BR") : ""}`
        : `✓ Colocada no rodízio${f.until ? " até " + new Date(f.until + "T12:00:00").toLocaleDateString("pt-BR") : ""}`);
      setSchedOpen(false);
    } catch (e) { setSchedMsg("✗ " + e.message); }
    setSchedSaving(false);
  }

  // ─── Repetir locução já gerada — sem chamar Gemini ────────────────────────
  function repeatTts() {
    if (!ttsUrl) return;
    send({ type: "cmd", action: "play_announcement", url: ttsUrl, title: "🔁 Locução ao vivo" });
    setTtsMsg("✓ Repetindo locução!");
    setTimeout(() => setTtsMsg(""), 2000);
  }

  // ─── Funções de gravação ──────────────────────────────────────────────────
  async function startRecording() {
    try {
      const stream = await navigator.mediaDevices.getUserMedia({ audio: true });
      chunksRef.current = [];
      const mr = new MediaRecorder(stream);
      mediaRecRef.current = mr;
      mr.ondataavailable = e => { if (e.data.size > 0) chunksRef.current.push(e.data); };
      mr.onstop = () => {
        const blob = new Blob(chunksRef.current, { type: "audio/webm" });
        setRecBlob(blob);
        setRecUrl(URL.createObjectURL(blob));
        setRecState("preview");
        stream.getTracks().forEach(t => t.stop());
      };
      mr.start();
      setRecState("recording");
      setRecMsg("");
    } catch {
      setRecMsg("✗ Permissão de microfone negada. Verifique as configurações do celular.");
    }
  }

  function stopRecording() {
    mediaRecRef.current?.stop();
  }

  function discardRecording() {
    setRecState("idle");
    setRecUrl(null);
    setRecBlob(null);
    setRecMsg("");
  }

  async function sendRecording() {
    if (!recBlob) return;
    setRecState("sending"); setRecMsg("");
    try {
      const formData = new FormData();
      formData.append("audio", recBlob, `voice-${Date.now()}.webm`);
      formData.append("storeId", storeId);
      formData.append("token", storeToken.current);
      const res = await fetch(`${API_BASE}/remote/voice`, { method: "POST", body: formData });
      const data = await res.json();
      if (!res.ok) throw new Error(data.error || "Erro ao enviar");
      send({ type: "cmd", action: "play_announcement", url: data.url, title: "🎤 Voz ao vivo" });
      setRecSavedUrl(data.url); // Salva URL para repetir
      setRecMsg("✓ Voz enviada para a loja!");
      setTimeout(() => setRecMsg(""), 3000);
      setRecState("idle");
      setRecUrl(null);
      setRecBlob(null);
    } catch (e) {
      setRecMsg("✗ " + e.message);
      setRecState("preview");
    }
  }

  // Limpa URL guardada se o texto, voz ou estilo mudar
  function handleTtsTextChange(e) {
    setTtsText(e.target.value);
    setTtsUrl(null);
  }
  function handleVoiceChange(e) {
    setTtsVoice(e.target.value);
    setTtsUrl(null);
  }
  function handleStyleChange(e) {
    setTtsStyle(e.target.value);
    setTtsUrl(null);
  }

  const VOICES = ["Aoede","Charon","Fenrir","Kore","Puck","Leda","Orus","Zephyr","Autonoe","Umbriel"];
  const STYLES = [
    { id: "warm",         label: "Caloroso" },
    { id: "professional", label: "Profissional" },
    { id: "energetic",    label: "Animado" },
    { id: "calm",         label: "Calmo" },
    { id: "luxury",       label: "Elegante" },
    { id: "promotional",  label: "Promocional" },
  ];

  // ─── TELA DE CONFIGURAÇÃO (quando abre sem store ID — ex: PWA no iOS) ────────
  const [setupLink, setSetupLink] = useState("");
  const [setupError, setSetupError] = useState("");

  function handleSetupLink() {
    setSetupError("");
    // Aceita URL completa ou só o ID
    let id = setupLink.trim();
    const match = id.match(/[0-9a-f]{8}-[0-9a-f]{4}-[0-9a-f]{4}-[0-9a-f]{4}-[0-9a-f]{12}/i);
    if (match) {
      id = match[0];
      localStorage.setItem("radioin_remote_store", id);
      window.location.href = `/remote?store=${id}`;
    } else {
      setSetupError("Link ou ID inválido. Cole o link completo que recebeu.");
    }
  }

  if (!storeId) return (
    <div style={s.root}>
      <div style={s.card}>
        <div style={s.logo}>📻 <span style={{ color: "#00ff87" }}>RadioIN</span></div>
        <h2 style={{ fontSize: 17, fontWeight: 700, marginBottom: 8, textAlign: "center" }}>Configurar Remoto</h2>
        <p style={{ fontSize: 13, color: "rgba(255,255,255,0.4)", marginBottom: 20, textAlign: "center" }}>
          Cole o link do remoto que você recebeu
        </p>
        <input
          type="url"
          placeholder="https://radioin.grupoproin.com.br/remote?store=..."
          value={setupLink}
          onChange={e => setSetupLink(e.target.value)}
          style={{
            width: "100%", padding: "12px 14px", background: "rgba(255,255,255,0.06)",
            border: "1px solid rgba(255,255,255,0.15)", borderRadius: 10,
            color: "#fff", fontSize: 13, marginBottom: 12, boxSizing: "border-box",
          }}
        />
        {setupError && <p style={{ color: "#ff4466", fontSize: 12, marginBottom: 10, textAlign: "center" }}>{setupError}</p>}
        <button onClick={handleSetupLink} style={{
          width: "100%", padding: 14, background: "linear-gradient(135deg,#00ff87,#00d4ff)",
          border: "none", borderRadius: 10, color: "#050810", fontWeight: 700, fontSize: 15, cursor: "pointer",
        }}>
          Conectar
        </button>
      </div>
    </div>
  );

  if (phase === "pin") return (
    <div style={s.root}>
      <div style={s.card}>
        <div style={s.logo}>📻 <span style={{ color: "#00ff87" }}>RadioIN</span></div>
        {multiStores.length > 1 && (
          <select value={storeId} onChange={e => switchStore(e.target.value)} style={{ width: "100%", marginBottom: 16, background: "#1a2a1a", border: "1px solid rgba(0,255,135,0.3)", borderRadius: 10, color: "#fff", padding: "10px 12px", fontSize: 14, fontWeight: 700, colorScheme: "dark" }}>
            {multiStores.map(st => <option key={st.id} value={st.id} style={{ background: "#1a2a1a", color: "#fff" }}>{st.name}</option>)}
          </select>
        )}
        <h2 style={{ fontSize: 18, fontWeight: 700, marginBottom: 4, textAlign: "center" }}>Controle Remoto</h2>
        <p style={{ fontSize: 13, color: "rgba(255,255,255,0.4)", marginBottom: 24, textAlign: "center" }}>Digite o PIN de 4 dígitos da loja</p>

        {/* Dígitos grandes */}
        <div style={{ display: "flex", gap: 12, justifyContent: "center", marginBottom: 20 }}>
          {[0,1,2,3].map(i => (
            <div key={i} style={{
              width: 52, height: 62, background: "rgba(255,255,255,0.05)",
              border: `2px solid ${pin.length === i ? "#00ff87" : pin[i] ? "rgba(0,255,135,0.3)" : "rgba(255,255,255,0.1)"}`,
              borderRadius: 10, display: "flex", alignItems: "center", justifyContent: "center",
              fontSize: 26, fontWeight: 700, color: pin[i] ? "#fff" : "transparent",
              transition: "border-color 0.2s",
            }}>
              {pin[i] ? "●" : ""}
            </div>
          ))}
        </div>

        {/* Teclado numérico */}
        <div style={{ display: "grid", gridTemplateColumns: "repeat(3, 1fr)", gap: 10, width: 220, margin: "0 auto 16px" }}>
          {[1,2,3,4,5,6,7,8,9,"",0,"⌫"].map((k, idx) => (
            <button key={idx} onClick={() => {
              if (k === "⌫") setPin(p => p.slice(0, -1));
              else if (k !== "" && pin.length < 4) setPin(p => p + k);
            }} style={{
              height: 56, background: k === "⌫" ? "rgba(255,68,68,0.1)" : "rgba(255,255,255,0.06)",
              border: `1px solid ${k === "⌫" ? "rgba(255,68,68,0.3)" : "rgba(255,255,255,0.1)"}`,
              borderRadius: 10, color: k === "⌫" ? "#ff6666" : "#fff",
              fontSize: k === "⌫" ? 18 : 20, fontWeight: 600, cursor: k === "" ? "default" : "pointer",
              visibility: k === "" ? "hidden" : "visible",
            }}>
              {k}
            </button>
          ))}
        </div>

        {pinError && <div style={{ color: "#ff4466", fontSize: 13, textAlign: "center", marginBottom: 12 }}>{pinError}</div>}

        <button onClick={submitPin} disabled={pin.length !== 4 || pinLoading} style={{
          width: "100%", padding: "14px", background: pin.length === 4 ? "#00ff87" : "rgba(255,255,255,0.06)",
          color: pin.length === 4 ? "#050810" : "rgba(255,255,255,0.3)",
          border: "none", borderRadius: 10, fontWeight: 700, fontSize: 15, cursor: pin.length === 4 ? "pointer" : "default",
          transition: "all 0.2s",
        }}>
          {pinLoading ? "Verificando..." : "Entrar"}
        </button>
      </div>
    </div>
  );

  // ─── TELA DO REMOTO ───────────────────────────────────────────────────────
  const isPlaying = playerState?.playing ?? false;

  return (
    <div style={s.root}>
      {/* Header */}
      <div style={s.header}>
        <div>
          <div style={s.logo}>📻 <span style={{ color: "#00ff87" }}>RadioIN</span></div>
          {multiStores.length > 1 ? (
            <select value={storeId} onChange={e => switchStore(e.target.value)} style={{ marginTop: 4, maxWidth: 210, background: "#1a2a1a", border: "1px solid rgba(0,255,135,0.3)", borderRadius: 8, color: "#fff", padding: "6px 8px", fontSize: 12, fontWeight: 700, colorScheme: "dark" }}>
              {multiStores.map(st => <option key={st.id} value={st.id} style={{ background: "#1a2a1a", color: "#fff" }}>{st.name}</option>)}
            </select>
          ) : (
            <div style={{ fontSize: 12, color: "rgba(255,255,255,0.4)", marginTop: 2 }}>{storeName}</div>
          )}
        </div>
        <div style={{ display: "flex", alignItems: "center", gap: 6 }}>
          <div style={{ width: 7, height: 7, borderRadius: "50%", background: connected ? "#00ff87" : "#ff4466", boxShadow: connected ? "0 0 8px #00ff87" : "none" }} />
          <span style={{ fontSize: 11, color: connected ? "#00ff87" : "#ff4466", fontFamily: "monospace" }}>
            {connected ? "CONECTADO" : "RECONECTANDO..."}
          </span>
        </div>
      </div>

      {/* Faixa tocando */}
      {playerState && (
        <div style={{ margin: "0 16px 16px", background: "rgba(0,255,135,0.05)", border: "1px solid rgba(0,255,135,0.15)", borderRadius: 12, padding: "14px 16px" }}>
          <div style={{ fontSize: 10, color: "rgba(0,255,135,0.6)", letterSpacing: 2, marginBottom: 4 }}>TOCANDO AGORA</div>
          <div style={{ fontSize: 15, fontWeight: 700, color: "#fff", overflow: "hidden", textOverflow: "ellipsis", whiteSpace: "nowrap" }}>{playerState.title || "—"}</div>
        </div>
      )}

      {/* Tabs */}
      <div style={{ display: "flex", gap: 4, margin: "0 16px 16px", background: "rgba(255,255,255,0.04)", borderRadius: 10, padding: 4 }}>
        {[["controls","🎛 Controles"],["tts","🎙 TTS"],["car","🚗 Veículo"],["saved","📋 Salvas"],["grades","🎚 Grades"]].map(([id, label]) => (
          <button key={id} onClick={() => setTab(id)} style={{
            flex: 1, padding: "9px 4px", background: tab === id ? "rgba(0,255,135,0.15)" : "transparent",
            border: tab === id ? "1px solid rgba(0,255,135,0.3)" : "1px solid transparent",
            borderRadius: 8, color: tab === id ? "#00ff87" : "rgba(255,255,255,0.4)",
            fontSize: 11, fontWeight: 600, cursor: "pointer", transition: "all 0.2s",
          }}>{label}</button>
        ))}
      </div>

      {/* ── TAB: CONTROLES ── */}
      {tab === "controls" && (
        <div style={{ padding: "0 16px", display: "flex", flexDirection: "column", gap: 20 }}>
          {/* Play/Pause + Skip */}
          <div style={s.section}>
            <div style={s.sectionTitle}>REPRODUÇÃO</div>
            <div style={{ display: "flex", gap: 16, justifyContent: "center", marginTop: 12 }}>
              <button onClick={cmdToggle} style={{
                width: 72, height: 72, borderRadius: "50%",
                background: isPlaying ? "rgba(0,255,135,0.12)" : "linear-gradient(135deg,#00ff87,#00d4ff)",
                border: isPlaying ? "2px solid rgba(0,255,135,0.4)" : "none",
                color: isPlaying ? "#00ff87" : "#050810", cursor: "pointer",
                boxShadow: isPlaying ? "0 0 16px rgba(0,255,135,0.2)" : "0 0 24px rgba(0,255,135,0.4)",
                display: "flex", alignItems: "center", justifyContent: "center",
              }}>
                {isPlaying ? (
                  <svg width="28" height="28" viewBox="0 0 24 24" fill="currentColor">
                    <rect x="5" y="4" width="4" height="16" rx="1"/>
                    <rect x="15" y="4" width="4" height="16" rx="1"/>
                  </svg>
                ) : (
                  <svg width="28" height="28" viewBox="0 0 24 24" fill="currentColor">
                    <polygon points="6,4 20,12 6,20"/>
                  </svg>
                )}
              </button>
              <button onClick={cmdSkip} style={{
                width: 72, height: 72, borderRadius: "50%",
                background: "rgba(0,255,135,0.08)",
                border: "2px solid rgba(0,255,135,0.3)",
                color: "#00ff87", cursor: "pointer",
                boxShadow: "0 0 12px rgba(0,255,135,0.1)",
                display: "flex", alignItems: "center", justifyContent: "center",
              }}>
                <svg width="26" height="26" viewBox="0 0 24 24" fill="currentColor">
                  <polygon points="5,4 15,12 5,20"/>
                  <rect x="16" y="4" width="3" height="16" rx="1"/>
                </svg>
              </button>
            </div>
          </div>

          {/* Volume */}
          <div style={s.section}>
            <div style={{ display: "flex", justifyContent: "space-between", alignItems: "center" }}>
              <div style={s.sectionTitle}>VOLUME</div>
              <span style={{ fontSize: 13, fontWeight: 700, color: "#00ff87", fontFamily: "monospace" }}>{volume}%</span>
            </div>
            <input type="range" min={0} max={100} value={volume}
              onChange={e => cmdVolume(Number(e.target.value))}
              style={{ width: "100%", marginTop: 12, accentColor: "#00ff87" }} />
            <div style={{ display: "flex", justifyContent: "space-between", marginTop: 4 }}>
              <span style={{ fontSize: 10, color: "rgba(255,255,255,0.3)" }}>🔈</span>
              <span style={{ fontSize: 10, color: "rgba(255,255,255,0.3)" }}>🔊</span>
            </div>
          </div>
        </div>
      )}

      {/* ── TAB: TTS RÁPIDO ── */}
      {tab === "tts" && (
        <div style={{ padding: "0 16px", display: "flex", flexDirection: "column", gap: 14 }}>
          <NoticeBox on={notice.tts} />
          <div style={s.section}>
            <div style={s.sectionTitle}>TEXTO DA LOCUÇÃO</div>
            <textarea value={ttsText} onChange={handleTtsTextChange}
              placeholder="Ex: Atenção clientes, o pão de queijo acabou de sair do forno!"
              style={{
                width: "100%", marginTop: 10, height: 90, resize: "none",
                background: "rgba(255,255,255,0.05)", border: "1px solid rgba(255,255,255,0.1)",
                borderRadius: 10, color: "#fff", fontSize: 14, padding: "10px 12px",
                fontFamily: "inherit", outline: "none",
              }} />
          </div>

          <div style={{ display: "grid", gridTemplateColumns: "1fr 1fr", gap: 10 }}>
            <div style={s.section}>
              <div style={s.sectionTitle}>VOZ</div>
              <select value={ttsVoice} onChange={handleVoiceChange}
                style={{ width: "100%", marginTop: 8, background: "#1a2a1a", border: "1px solid rgba(255,255,255,0.1)", borderRadius: 8, color: "#fff", padding: "8px 10px", fontSize: 13, colorScheme: "dark" }}>
                {VOICES.map(v => <option key={v} value={v} style={{ background: "#1a2a1a", color: "#fff" }}>{v}</option>)}
              </select>
            </div>
            <div style={s.section}>
              <div style={s.sectionTitle}>ESTILO</div>
              <select value={ttsStyle} onChange={handleStyleChange}
                style={{ width: "100%", marginTop: 8, background: "#1a2a1a", border: "1px solid rgba(255,255,255,0.1)", borderRadius: 8, color: "#fff", padding: "8px 10px", fontSize: 13, colorScheme: "dark" }}>
                {STYLES.map(st => <option key={st.id} value={st.id} style={{ background: "#1a2a1a", color: "#fff" }}>{st.label}</option>)}
              </select>
            </div>
          </div>

          {ttsMsg && (
            <div style={{ padding: "10px 14px", background: ttsMsg.startsWith("✓") ? "rgba(0,255,135,0.1)" : "rgba(255,68,68,0.1)", border: `1px solid ${ttsMsg.startsWith("✓") ? "rgba(0,255,135,0.3)" : "rgba(255,68,68,0.3)"}`, borderRadius: 8, fontSize: 13, color: ttsMsg.startsWith("✓") ? "#00ff87" : "#ff6666", textAlign: "center" }}>
              {ttsMsg}
            </div>
          )}

          <button onClick={sendTts} disabled={!ttsText.trim() || ttsSending || notice.tts} style={{
            padding: "16px", background: ttsText.trim() ? "linear-gradient(135deg,#00ff87,#00d4ff)" : "rgba(255,255,255,0.06)",
            color: ttsText.trim() ? "#050810" : "rgba(255,255,255,0.3)",
            border: "none", borderRadius: 12, fontWeight: 700, fontSize: 15, cursor: ttsText.trim() ? "pointer" : "default",
            boxShadow: ttsText.trim() ? "0 0 20px rgba(0,255,135,0.3)" : "none",
            transition: "all 0.2s",
          }}>
            {ttsSending ? "⏳ Gerando e enviando..." : "🎙 Fazer Locução na Loja"}
          </button>

          {ttsUrl && (
            <div style={{ display: "flex", gap: 8 }}>
              <button onClick={repeatTts} style={{
                flex: 1, padding: "12px",
                background: "rgba(0,255,135,0.1)",
                border: "1px solid rgba(0,255,135,0.3)", borderRadius: 12,
                color: "#00ff87", fontWeight: 700, fontSize: 14, cursor: "pointer",
              }}>
                🔁 Repetir locução
              </button>
              <button onClick={() => { setTtsText(""); setTtsUrl(null); setTtsMsg(""); setTtsLast(null); setSchedOpen(false); setSchedMsg(""); }} style={{
                padding: "12px 16px", background: "rgba(255,255,255,0.05)",
                border: "1px solid rgba(255,255,255,0.1)", borderRadius: 12,
                color: "rgba(255,255,255,0.4)", fontSize: 14, cursor: "pointer",
              }}>
                🗑
              </button>
            </div>
          )}

          {/* ── 📅 Programar esta locução ── */}
          {ttsLast && !notice.tts && (() => {
            const DIAS = ["Dom", "Seg", "Ter", "Qua", "Qui", "Sex", "Sáb"];
            const f = schedForm;
            const setF = (patch) => setSchedForm(x => ({ ...x, ...patch }));
            const inp = { width: "100%", boxSizing: "border-box", background: "#1a2a1a", border: "1px solid rgba(255,255,255,0.12)", borderRadius: 8, color: "#fff", padding: "9px 10px", fontSize: 14, colorScheme: "dark" };
            const chip = (on) => ({ padding: "7px 8px", borderRadius: 8, fontSize: 12, fontWeight: 700, cursor: "pointer", flex: 1,
              background: on ? "rgba(0,255,135,0.15)" : "transparent", color: on ? "#00ff87" : "rgba(255,255,255,0.45)", border: on ? "1px solid rgba(0,255,135,0.35)" : "1px solid rgba(255,255,255,0.1)" });
            const addTime = () => { const t = f.timeInput; if (/^([01]\d|2[0-3]):[0-5]\d$/.test(t) && !f.times.includes(t)) setF({ times: [...f.times, t].sort(), timeInput: "" }); };
            return (
              <div>
                {!schedOpen ? (
                  <button onClick={() => { setSchedOpen(true); setSchedMsg(""); }} style={{ width: "100%", padding: "12px", background: "rgba(59,130,246,0.12)", border: "1px solid rgba(59,130,246,0.35)", borderRadius: 12, color: "#60a5fa", fontWeight: 700, fontSize: 14, cursor: "pointer" }}>
                    📅 Programar esta locução
                  </button>
                ) : (
                  <div style={{ ...s.section, display: "flex", flexDirection: "column", gap: 12 }}>
                    <div style={s.sectionTitle}>PROGRAMAR LOCUÇÃO</div>
                    <input value={f.title} onChange={e => setF({ title: e.target.value })} maxLength={120} placeholder="Nome da locução" style={inp} />
                    <div style={{ display: "flex", gap: 6 }}>
                      <button onClick={() => setF({ mode: "schedule" })} style={chip(f.mode === "schedule")}>⏰ Em horários</button>
                      <button onClick={() => setF({ mode: "rotation" })} style={chip(f.mode === "rotation")}>🔄 No rodízio</button>
                    </div>
                    {f.mode === "schedule" ? (
                      <>
                        <div style={{ display: "flex", gap: 6 }}>
                          <input type="time" value={f.timeInput} onChange={e => setF({ timeInput: e.target.value })} style={{ ...inp, flex: 1 }} />
                          <button onClick={addTime} style={{ padding: "0 14px", background: "rgba(0,255,135,0.15)", border: "1px solid rgba(0,255,135,0.35)", borderRadius: 8, color: "#00ff87", fontWeight: 800, cursor: "pointer" }}>+ Horário</button>
                        </div>
                        {f.times.length > 0 && (
                          <div style={{ display: "flex", gap: 6, flexWrap: "wrap" }}>
                            {f.times.map(t => (
                              <span key={t} onClick={() => setF({ times: f.times.filter(x => x !== t) })} title="Remover" style={{ padding: "5px 10px", background: "rgba(255,187,51,0.12)", border: "1px solid rgba(255,187,51,0.4)", borderRadius: 20, color: "#ffbb33", fontSize: 13, cursor: "pointer" }}>⏰ {t} ✕</span>
                            ))}
                          </div>
                        )}
                        <div style={{ display: "flex", gap: 4 }}>
                          {DIAS.map((d, i) => { const on = f.days.includes(String(i)); return (
                            <button key={i} onClick={() => setF({ days: on ? f.days.filter(x => x !== String(i)) : [...f.days, String(i)] })} style={{ ...chip(on), padding: "6px 0", fontSize: 11 }}>{d}</button>
                          ); })}
                        </div>
                      </>
                    ) : (
                      <div>
                        <select value={f.categoryId} onChange={e => setF({ categoryId: e.target.value })} style={inp}>
                          <option value="">Sem categoria (rodízio geral)</option>
                          {announcementCategories.map(c => <option key={c.id} value={c.id}>{c.icon} {c.name}</option>)}
                        </select>
                        <div style={{ fontSize: 11, color: "rgba(255,255,255,0.35)", marginTop: 6, lineHeight: 1.5 }}>Toca quando a grade da loja chegar num bloco dessa categoria.</div>
                      </div>
                    )}
                    <div>
                      <div style={{ fontSize: 11, color: "rgba(255,255,255,0.4)", marginBottom: 4 }}>Até quando? (opcional)</div>
                      <input type="date" value={f.until} min={new Date().toLocaleDateString("sv-SE")} onChange={e => setF({ until: e.target.value })} style={inp} />
                    </div>
                    <div style={{ display: "flex", gap: 8 }}>
                      <button onClick={saveSchedTts} disabled={schedSaving} style={{ flex: 2, padding: "12px", background: "linear-gradient(135deg,#00ff87,#00d4ff)", border: "none", borderRadius: 10, color: "#050810", fontWeight: 800, fontSize: 14, cursor: "pointer" }}>
                        {schedSaving ? "Salvando..." : "Salvar programação"}
                      </button>
                      <button onClick={() => setSchedOpen(false)} style={{ flex: 1, padding: "12px", background: "transparent", border: "1px solid rgba(255,255,255,0.15)", borderRadius: 10, color: "rgba(255,255,255,0.6)", fontSize: 14, cursor: "pointer" }}>Cancelar</button>
                    </div>
                  </div>
                )}
                {schedMsg && (
                  <div style={{ marginTop: 8, padding: "10px 14px", background: schedMsg.startsWith("✓") ? "rgba(0,255,135,0.1)" : "rgba(255,68,68,0.1)", border: `1px solid ${schedMsg.startsWith("✓") ? "rgba(0,255,135,0.3)" : "rgba(255,68,68,0.3)"}`, borderRadius: 8, fontSize: 13, color: schedMsg.startsWith("✓") ? "#00ff87" : "#ff6666", textAlign: "center" }}>{schedMsg}</div>
                )}
              </div>
            );
          })()}
        </div>
      )}

      {/* ── TAB: ANÚNCIO DE VEÍCULO ── */}
      {tab === "car" && (() => {
        const sel = { width: "100%", marginTop: 8, background: "#1a2a1a", border: "1px solid rgba(255,255,255,0.1)", borderRadius: 8, color: "#fff", padding: "10px", fontSize: 14, colorScheme: "dark" };
        const opt = { background: "#1a2a1a", color: "#fff" };
        const seg = (active) => ({ flex: 1, padding: "8px 4px", borderRadius: 8, fontSize: 13, fontWeight: 700, cursor: "pointer",
          background: active ? "rgba(0,255,135,0.15)" : "transparent", color: active ? "#00ff87" : "rgba(255,255,255,0.45)",
          border: active ? "1px solid rgba(0,255,135,0.35)" : "1px solid rgba(255,255,255,0.08)" });
        const set = (k, v) => setCarForm(f => ({ ...f, [k]: v }));
        if (notice.car) return <div style={{ padding: "0 16px" }}><NoticeBox on /></div>;
        if (!carOpts) return <div style={{ padding: 30, textAlign: "center", color: "rgba(255,255,255,0.4)" }}>Carregando...</div>;
        if (!carOpts.ready) return (
          <div style={{ padding: "0 16px" }}>
            <div style={{ ...s.section, textAlign: "center", color: "rgba(255,255,255,0.55)", fontSize: 13, lineHeight: 1.6 }}>
              🚗 Os áudios de anúncio de veículos ainda estão sendo preparados.<br />Tente novamente mais tarde.
              {carOpts.error && <div style={{ color: "#ff6666", marginTop: 6 }}>{carOpts.error}</div>}
              <button onClick={() => { setCarOpts(null); loadCarOptions(); }} style={{ marginTop: 12, padding: "8px 16px", background: "transparent", border: "1px solid rgba(255,255,255,0.15)", borderRadius: 8, color: "#fff", cursor: "pointer" }}>Tentar de novo</button>
            </div>
          </div>
        );
        return (
          <div style={{ padding: "0 16px", display: "flex", flexDirection: "column", gap: 12 }}>
            <div style={{ display: "grid", gridTemplateColumns: "1fr 1fr", gap: 10 }}>
              <div style={s.section}>
                <div style={s.sectionTitle}>TIPO</div>
                <select value={carForm.tipo} onChange={e => set("tipo", e.target.value)} style={sel}>
                  {carOpts.tipos.map(o => <option key={o.value} value={o.value} style={opt}>{o.label}</option>)}
                </select>
              </div>
              <div style={s.section}>
                <div style={s.sectionTitle}>MARCA</div>
                <select value={carForm.marca} onChange={e => setCarForm(f => ({ ...f, marca: e.target.value, modelo: "" }))} style={sel}>
                  <option value="" style={opt}>Não informar</option>
                  {carOpts.marcas.map(o => <option key={o.value} value={o.value} style={opt}>{o.label}</option>)}
                </select>
              </div>
              {(() => {
                const mods = (carOpts.modelos || []).filter(m => m.marca === carForm.marca);
                return (
                  <div style={{ ...s.section, opacity: mods.length ? 1 : 0.5 }}>
                    <div style={s.sectionTitle}>MODELO</div>
                    <select value={carForm.modelo} disabled={!mods.length} onChange={e => set("modelo", e.target.value)} style={sel}>
                      <option value="" style={opt}>{!carForm.marca ? "Escolha a marca" : mods.length ? "Não informar" : "Sem modelos"}</option>
                      {mods.map(o => <option key={o.value} value={o.value} style={opt}>{o.label}</option>)}
                    </select>
                  </div>
                );
              })()}
              <div style={s.section}>
                <div style={s.sectionTitle}>COR</div>
                <select value={carForm.cor} onChange={e => set("cor", e.target.value)} style={sel}>
                  <option value="" style={opt}>Não informar</option>
                  {carOpts.cores.map(o => <option key={o.value} value={o.value} style={opt}>{o.label}</option>)}
                </select>
              </div>
              <div style={{ ...s.section, gridColumn: "1 / -1" }}>
                <div style={s.sectionTitle}>PLACA</div>
                <input value={carForm.placa} maxLength={8} placeholder="ABC1D23" autoCapitalize="characters"
                  onChange={e => set("placa", e.target.value.toUpperCase().replace(/[^A-Z0-9-]/g, ""))}
                  style={{ ...sel, fontFamily: "monospace", letterSpacing: 2, fontSize: 16, borderColor: carForm.placa && !placaOk ? "rgba(255,68,68,0.6)" : "rgba(255,255,255,0.1)" }} />
              </div>
            </div>

            <div style={s.section}>
              <div style={{ display: "flex", alignItems: "center", justifyContent: "space-between", gap: 10 }}>
                <div>
                  <div style={{ fontSize: 13, fontWeight: 700 }}>Sinal sonoro antes</div>
                  <div style={{ fontSize: 11, color: "rgba(255,255,255,0.35)" }}>gongo curto para chamar atenção</div>
                </div>
                <div style={{ display: "flex", gap: 6, width: 130 }}>
                  <button onClick={() => set("gongo", true)} style={seg(carForm.gongo)}>Sim</button>
                  <button onClick={() => set("gongo", false)} style={seg(!carForm.gongo)}>Não</button>
                </div>
              </div>
              <div style={{ marginTop: 14 }}>
                <div style={{ fontSize: 13, fontWeight: 700 }}>Repetição</div>
                <div style={{ fontSize: 11, color: "rgba(255,255,255,0.35)", marginBottom: 8 }}>quantas vezes o chamado é repetido em seguida</div>
                <div style={{ display: "flex", gap: 6 }}>
                  {[[0, "Nenhuma"], [1, "1 vez"], [2, "2 vezes"]].map(([v, l]) => (
                    <button key={v} onClick={() => set("repeticoes", v)} style={seg(carForm.repeticoes === v)}>{l}</button>
                  ))}
                </div>
              </div>
            </div>

            {carMsg && (
              <div style={{ padding: "10px 14px", background: carMsg.startsWith("✓") ? "rgba(0,255,135,0.1)" : "rgba(255,68,68,0.1)", border: `1px solid ${carMsg.startsWith("✓") ? "rgba(0,255,135,0.3)" : "rgba(255,68,68,0.3)"}`, borderRadius: 8, fontSize: 13, color: carMsg.startsWith("✓") ? "#00ff87" : "#ff6666", textAlign: "center" }}>
                {carMsg}
              </div>
            )}

            <button onClick={sendCar} disabled={!placaOk || carSending} style={{
              padding: "16px", background: placaOk ? "linear-gradient(135deg,#00ff87,#00d4ff)" : "rgba(255,255,255,0.06)",
              color: placaOk ? "#050810" : "rgba(255,255,255,0.3)", border: "none", borderRadius: 12,
              fontWeight: 700, fontSize: 15, cursor: placaOk ? "pointer" : "default",
            }}>
              {carSending ? "⏳ Montando anúncio..." : "🔊 Reproduzir agora"}
            </button>
            {carLastUrl && (
              <button onClick={repeatCar} style={{ padding: "12px", background: "transparent", border: "1px solid rgba(255,255,255,0.15)", borderRadius: 12, color: "#fff", fontWeight: 600, fontSize: 14, cursor: "pointer" }}>
                🔁 Repetir último anúncio
              </button>
            )}
          </div>
        );
      })()}

      {/* ── TAB: GRAVAR VOZ ── */}
      {tab === "voice" && (
        <div style={{ padding: "0 16px", display: "flex", flexDirection: "column", gap: 16 }}>
          <div style={s.section}>
            <div style={s.sectionTitle}>GRAVAÇÃO DE VOZ</div>
            <p style={{ fontSize: 12, color: "rgba(255,255,255,0.35)", marginTop: 8, lineHeight: 1.5 }}>
              Grave seu anúncio diretamente pelo celular e ele toca na loja na hora.
            </p>
          </div>

          {recState === "idle" && (
            <button onClick={startRecording} style={{
              padding: "20px", background: "rgba(255,68,68,0.1)",
              border: "2px solid rgba(255,68,68,0.4)", borderRadius: 16,
              color: "#ff6666", fontWeight: 700, fontSize: 18, cursor: "pointer",
              display: "flex", alignItems: "center", justifyContent: "center", gap: 10,
            }}>
              🎤 Pressione para Gravar
            </button>
          )}

          {recState === "recording" && (
            <div style={{ display: "flex", flexDirection: "column", gap: 12 }}>
              <div style={{ display: "flex", alignItems: "center", justifyContent: "center", gap: 10,
                padding: "16px", background: "rgba(255,68,68,0.15)", border: "2px solid rgba(255,68,68,0.5)",
                borderRadius: 16 }}>
                <div style={{ width: 12, height: 12, borderRadius: "50%", background: "#ff4444",
                  animation: "blink 0.8s ease-in-out infinite" }} />
                <span style={{ color: "#ff6666", fontWeight: 700, fontSize: 16 }}>Gravando...</span>
              </div>
              <button onClick={stopRecording} style={{
                padding: "16px", background: "rgba(255,255,255,0.08)",
                border: "1px solid rgba(255,255,255,0.2)", borderRadius: 12,
                color: "#fff", fontWeight: 700, fontSize: 15, cursor: "pointer",
              }}>
                ⏹ Parar Gravação
              </button>
            </div>
          )}

          {recState === "preview" && recUrl && (
            <div style={{ display: "flex", flexDirection: "column", gap: 12 }}>
              <div style={s.section}>
                <div style={s.sectionTitle}>OUÇA ANTES DE ENVIAR</div>
                <audio controls src={recUrl} style={{ width: "100%", marginTop: 10, borderRadius: 8 }} />
              </div>
              <button onClick={sendRecording} style={{
                padding: "16px", background: "linear-gradient(135deg,#00ff87,#00d4ff)",
                border: "none", borderRadius: 12, color: "#050810",
                fontWeight: 700, fontSize: 15, cursor: "pointer",
                boxShadow: "0 0 20px rgba(0,255,135,0.3)",
              }}>
                📤 Enviar para a Loja
              </button>
              <button onClick={discardRecording} style={{
                padding: "12px", background: "rgba(255,68,68,0.1)",
                border: "1px solid rgba(255,68,68,0.3)", borderRadius: 12,
                color: "#ff6666", fontWeight: 600, fontSize: 14, cursor: "pointer",
              }}>
                🗑 Descartar e Gravar Novamente
              </button>
            </div>
          )}

          {recState === "sending" && (
            <div style={{ textAlign: "center", padding: "24px", color: "rgba(255,255,255,0.5)", fontSize: 14 }}>
              ⏳ Enviando para a loja...
            </div>
          )}

          {recMsg && (
            <div style={{ padding: "10px 14px",
              background: recMsg.startsWith("✓") ? "rgba(0,255,135,0.1)" : "rgba(255,68,68,0.1)",
              border: `1px solid ${recMsg.startsWith("✓") ? "rgba(0,255,135,0.3)" : "rgba(255,68,68,0.3)"}`,
              borderRadius: 8, fontSize: 13,
              color: recMsg.startsWith("✓") ? "#00ff87" : "#ff6666", textAlign: "center" }}>
              {recMsg}
            </div>
          )}

          {/* Botão repetir — aparece após enviar, some quando gravar de novo */}
          {recSavedUrl && recState === "idle" && (
            <div style={{ background: "rgba(0,255,135,0.06)", border: "1px solid rgba(0,255,135,0.2)", borderRadius: 12, padding: "14px 16px" }}>
              <div style={{ fontSize: 11, color: "rgba(255,255,255,0.4)", marginBottom: 8, letterSpacing: 1 }}>ÚLTIMA GRAVAÇÃO</div>
              <div style={{ display: "flex", gap: 10 }}>
                <button onClick={() => send({ type: "cmd", action: "play_announcement", url: recSavedUrl, title: "🎤 Voz ao vivo" })}
                  style={{ flex: 1, padding: "10px", background: "rgba(0,255,135,0.15)", border: "1px solid rgba(0,255,135,0.3)", borderRadius: 8, color: "#00ff87", fontWeight: 700, fontSize: 13, cursor: "pointer" }}>
                  🔁 Repetir na loja
                </button>
                <button onClick={() => setRecSavedUrl(null)}
                  style={{ padding: "10px 12px", background: "rgba(255,68,68,0.1)", border: "1px solid rgba(255,68,68,0.3)", borderRadius: 8, color: "#ff6666", fontSize: 13, cursor: "pointer" }}>
                  🗑
                </button>
              </div>
            </div>
          )}
        </div>
      )}

      {/* ── TAB: LOCUÇÕES CADASTRADAS ── */}
      {tab === "saved" && (
        <div style={{ padding: "0 16px", overflowY: "auto", maxHeight: "calc(100vh - 240px)" }}>
          {announcements.length > 0 && (
            <div style={{ position: "sticky", top: 0, zIndex: 2, background: "#050810", paddingBottom: 10 }}>
              <input value={savedSearch} onChange={e => setSavedSearch(e.target.value)} placeholder="🔎 Buscar locução..."
                style={{ width: "100%", boxSizing: "border-box", background: "rgba(255,255,255,0.06)", border: "1px solid rgba(255,255,255,0.12)", borderRadius: 10, color: "#fff", fontSize: 14, padding: "11px 14px", outline: "none" }} />
            </div>
          )}
          {announcements.length === 0 ? (
            <div style={{ textAlign: "center", padding: "40px 20px", color: "rgba(255,255,255,0.3)" }}>
              <div style={{ fontSize: 36, marginBottom: 12 }}>🎙</div>
              <p style={{ fontSize: 13 }}>Nenhuma locução cadastrada ainda.</p>
            </div>
          ) : (() => {
            // Sem repetidos (cópias entre lojas usam o mesmo áudio) + busca sem acento
            const seen = new Set();
            const unique = announcements.filter(a => { const k = a.bunny_url || a.id; if (seen.has(k)) return false; seen.add(k); return true; });
            const norm = (t) => (t || "").normalize("NFD").replace(/[\u0300-\u036f]/g, "").toLowerCase();
            const q = norm(savedSearch.trim());
            const list = q ? unique.filter(a => norm(a.title).includes(q)) : unique;

            // Agrupa por categoria
            const semCat = list.filter(a => !a.announcement_category_id);
            const porCat = announcementCategories.map(cat => ({
              cat,
              items: list.filter(a => a.announcement_category_id === cat.id)
            })).filter(g => g.items.length > 0);

            const renderItem = (a) => (
              <div key={a.id} style={{ background: "rgba(255,255,255,0.04)", border: "1px solid rgba(255,255,255,0.08)", borderRadius: 10, padding: "12px 14px", display: "flex", alignItems: "center", gap: 12, marginBottom: 6 }}>
                <div style={{ flex: 1, overflow: "hidden" }}>
                  <div style={{ fontSize: 13, fontWeight: 600, color: "#fff", overflow: "hidden", textOverflow: "ellipsis", whiteSpace: "nowrap" }}>{a.title}</div>
                  <div style={{ fontSize: 11, color: "rgba(255,255,255,0.3)", marginTop: 2 }}>{a.voice_name !== "upload" ? `${a.voice_name} · ` : ""}{a.duration_seconds || 0}s</div>
                </div>
                <button onClick={() => playAnnouncement(a)} style={{
                  width: 40, height: 40, borderRadius: "50%",
                  background: "rgba(0,255,135,0.15)", border: "1px solid rgba(0,255,135,0.3)",
                  color: "#00ff87", fontSize: 16, cursor: "pointer", flexShrink: 0,
                }}>▶</button>
              </div>
            );

            if (q && list.length === 0) return (
              <div style={{ textAlign: "center", padding: "30px 20px", color: "rgba(255,255,255,0.35)", fontSize: 13 }}>Nenhuma locução encontrada para "{savedSearch}".</div>
            );

            return (
              <div style={{ display: "flex", flexDirection: "column", gap: 4, paddingBottom: 16 }}>
                {/* Grupos por categoria */}
                {porCat.map(({ cat, items }) => (
                  <div key={cat.id}>
                    <button onClick={() => setOpenCategories(prev => ({ ...prev, [cat.id]: !prev[cat.id] }))}
                      style={{ width: "100%", display: "flex", alignItems: "center", gap: 8, padding: "10px 14px", background: openCategories[cat.id] ? "rgba(255,255,255,0.08)" : "rgba(255,255,255,0.04)", border: "1px solid rgba(255,255,255,0.1)", borderRadius: 10, cursor: "pointer", marginBottom: 4 }}>
                      <span style={{ fontSize: 16 }}>{cat.icon}</span>
                      <span style={{ flex: 1, fontSize: 13, fontWeight: 700, color: "#fff", textAlign: "left" }}>{cat.name}</span>
                      <span style={{ fontSize: 11, color: "rgba(255,255,255,0.3)" }}>{items.length}</span>
                      <span style={{ fontSize: 12, color: "rgba(255,255,255,0.4)" }}>{openCategories[cat.id] ? "▲" : "▼"}</span>
                    </button>
                    {(openCategories[cat.id] || q) && (
                      <div style={{ paddingLeft: 8, marginBottom: 4 }}>
                        {items.map(a => renderItem(a))}
                      </div>
                    )}
                  </div>
                ))}
                {/* Sem categoria */}
                {semCat.length > 0 && (
                  <div>
                    <button onClick={() => setOpenCategories(prev => ({ ...prev, __none__: !prev.__none__ }))}
                      style={{ width: "100%", display: "flex", alignItems: "center", gap: 8, padding: "10px 14px", background: openCategories.__none__ ? "rgba(255,255,255,0.08)" : "rgba(255,255,255,0.04)", border: "1px solid rgba(255,255,255,0.1)", borderRadius: 10, cursor: "pointer", marginBottom: 4 }}>
                      <span style={{ fontSize: 16 }}>🎙</span>
                      <span style={{ flex: 1, fontSize: 13, fontWeight: 700, color: "#fff", textAlign: "left" }}>Geral</span>
                      <span style={{ fontSize: 11, color: "rgba(255,255,255,0.3)" }}>{semCat.length}</span>
                      <span style={{ fontSize: 12, color: "rgba(255,255,255,0.4)" }}>{openCategories.__none__ ? "▲" : "▼"}</span>
                    </button>
                    {openCategories.__none__ && (
                      <div style={{ paddingLeft: 8, marginBottom: 4 }}>
                        {semCat.map(a => renderItem(a))}
                      </div>
                    )}
                  </div>
                )}
              </div>
            );
          })()}
        </div>
      )}

      {/* ── TAB: GRADES ── */}
      {tab === "grades" && (
        <div style={{ padding: "0 16px" }}>
          {schedules.length === 0 ? (
            <div style={{ textAlign: "center", padding: "40px 20px", color: "rgba(255,255,255,0.3)" }}>
              <div style={{ fontSize: 36, marginBottom: 12 }}>🎚</div>
              <p style={{ fontSize: 13 }}>Nenhuma grade criada ainda.</p>
              <p style={{ fontSize: 11, marginTop: 8 }}>Crie grades no painel em Grade do Player.</p>
            </div>
          ) : (
            <div style={{ display: "flex", flexDirection: "column", gap: 10 }}>
              <div style={{ fontSize: 11, color: "rgba(255,255,255,0.3)", marginBottom: 4, letterSpacing: 1 }}>SELECIONE A PROGRAMAÇÃO</div>
              {schedules.map(sch => (
                <div key={sch.id}>
                  <div style={{ background: sch.is_active ? "rgba(0,255,135,0.08)" : "rgba(255,255,255,0.04)", border: `1px solid ${sch.is_active ? "rgba(0,255,135,0.3)" : "rgba(255,255,255,0.08)"}`, borderRadius: 12, padding: "14px 16px", display: "flex", alignItems: "center", gap: 12 }}>
                    {/* Dot ativo */}
                    <div style={{ width: 10, height: 10, borderRadius: "50%", background: sch.is_active ? "#00ff87" : "rgba(255,255,255,0.2)", boxShadow: sch.is_active ? "0 0 8px #00ff87" : "none", flexShrink: 0, animation: sch.is_active ? "pulse 2s infinite" : "none" }} />
                    <div style={{ flex: 1 }}>
                      <div style={{ fontSize: 15, fontWeight: 700, color: sch.is_active ? "#00ff87" : "#fff" }}>{sch.name}</div>
                      <div style={{ fontSize: 11, color: "rgba(255,255,255,0.3)", marginTop: 2 }}>{sch.is_active ? "● Ativa agora" : "Inativa"}</div>
                    </div>
                    {!sch.is_active && (
                      <button
                        onClick={() => setConfirmSwitch({ id: sch.id, name: sch.name })}
                        disabled={switchingSchedule === sch.id}
                        style={{ padding: "8px 14px", background: "rgba(0,255,135,0.15)", border: "1px solid rgba(0,255,135,0.3)", borderRadius: 8, color: "#00ff87", fontSize: 12, fontWeight: 700, cursor: "pointer" }}>
                        Selecionar
                      </button>
                    )}
                    {sch.is_active && (
                      <span style={{ fontSize: 11, color: "#00ff87", fontWeight: 700 }}>✓ Ativa</span>
                    )}
                  </div>

                  {/* Confirmação de troca */}
                  {confirmSwitch?.id === sch.id && (
                    <div style={{ background: "rgba(0,255,135,0.05)", border: "1px solid rgba(0,255,135,0.2)", borderRadius: 10, padding: "14px 16px", marginTop: 6 }}>
                      <div style={{ fontSize: 13, color: "rgba(255,255,255,0.7)", marginBottom: 12, textAlign: "center" }}>
                        Como trocar para <strong style={{ color: "#00ff87" }}>{sch.name}</strong>?
                      </div>
                      <div style={{ display: "flex", gap: 8 }}>
                        <button
                          onClick={() => {
                            setSwitchingSchedule(sch.id);
                            send({ type: "cmd", action: "switch_schedule", scheduleId: sch.id, storeId, immediate: false });
                            setSchedules(prev => prev.map(s => ({ ...s, is_active: s.id === sch.id })));
                            setConfirmSwitch(null);
                            setTimeout(() => setSwitchingSchedule(null), 2000);
                          }}
                          style={{ flex: 1, padding: "10px", background: "rgba(255,255,255,0.08)", border: "1px solid rgba(255,255,255,0.15)", borderRadius: 8, color: "#fff", fontSize: 12, fontWeight: 600, cursor: "pointer" }}>
                          ⏭ Na próxima música
                        </button>
                        <button
                          onClick={() => {
                            setSwitchingSchedule(sch.id);
                            send({ type: "cmd", action: "switch_schedule", scheduleId: sch.id, storeId, immediate: true });
                            setSchedules(prev => prev.map(s => ({ ...s, is_active: s.id === sch.id })));
                            setConfirmSwitch(null);
                            setTimeout(() => setSwitchingSchedule(null), 2000);
                          }}
                          style={{ flex: 1, padding: "10px", background: "linear-gradient(135deg,#00ff87,#00d4ff)", border: "none", borderRadius: 8, color: "#050810", fontSize: 12, fontWeight: 700, cursor: "pointer" }}>
                          ⚡ Agora
                        </button>
                      </div>
                      <button onClick={() => setConfirmSwitch(null)}
                        style={{ width: "100%", marginTop: 8, padding: "8px", background: "none", border: "none", color: "rgba(255,255,255,0.3)", fontSize: 12, cursor: "pointer" }}>
                        Cancelar
                      </button>
                    </div>
                  )}
                </div>
              ))}
            </div>
          )}
        </div>
      )}

      <div style={{ height: 24 }} />
    </div>
  );
}

const s = {
  root: {
    minHeight: "100vh", background: "#050810",
    fontFamily: "'Segoe UI', sans-serif", color: "#fff",
    display: "flex", flexDirection: "column",
  },
  card: {
    margin: "auto", width: "100%", maxWidth: 340,
    background: "rgba(255,255,255,0.04)", border: "1px solid rgba(255,255,255,0.08)",
    borderRadius: 20, padding: "32px 24px",
    backdropFilter: "blur(20px)",
  },
  logo: {
    fontSize: 18, fontWeight: 800, letterSpacing: 1,
    textAlign: "center", marginBottom: 20,
  },
  header: {
    display: "flex", alignItems: "center", justifyContent: "space-between",
    padding: "20px 16px 12px",
    borderBottom: "1px solid rgba(255,255,255,0.06)", marginBottom: 16,
  },
  section: {
    background: "rgba(255,255,255,0.04)", border: "1px solid rgba(255,255,255,0.07)",
    borderRadius: 12, padding: "14px 16px",
  },
  sectionTitle: {
    fontSize: 10, fontWeight: 700, letterSpacing: 2,
    color: "rgba(255,255,255,0.35)", fontFamily: "monospace",
  },
};
